/*
 * Purpose: Curated MCP server presets — well-known services, one-click registry entries
 * Last modified: 2026-07-11 | Agent
 * Completeness: 90
 * Server URLs verified via web search against vendor docs on 2026-07-11 — recheck
 * periodically, vendors have moved these endpoints before (see Asana v1→v2).
 */
import type { ConnectorCapability } from '@/services/connectors/library/types';

export type McpPresetAuthHint = 'oauth_via_provider' | 'api_token' | 'self_hosted';

export interface McpServerPreset {
  /** MCP server registry id — also used to derive connector id `ab.mcp.<id>` */
  id: string;
  name: string;
  category: string;
  description: string;
  /** Known public streamable-HTTP MCP endpoint, when the vendor hosts one */
  serverUrl?: string;
  /** True when there is no single well-known URL and the user must supply their own host */
  requiresCustomUrl?: boolean;
  authHint: McpPresetAuthHint;
  /** AutoBlocks-only setup copy — no third-party product steps beyond "get a token from X" */
  setupSteps: string[];
  docsUrl?: string;
  capabilities: ConnectorCapability[];
}

function tool(id: string, label: string, description: string): ConnectorCapability {
  return { id, kind: 'tool', label, blockType: 'tool', blockSubtype: 'mcp_tool', description };
}

export const MCP_SERVER_PRESETS: McpServerPreset[] = [
  {
    id: 'slack',
    name: 'Slack',
    category: 'messaging',
    description: 'Post messages, read channels, and search Slack via its remote MCP server.',
    serverUrl: 'https://mcp.slack.com/mcp',
    authHint: 'oauth_via_provider',
    docsUrl: 'https://modelcontextprotocol.io',
    setupSteps: [
      'Connect → MCP servers → Slack, then Connect to start the OAuth handshake.',
      'AutoBlocks stores the resulting token in your vault as MCP_SLACK_TOKEN.',
    ],
    capabilities: [
      tool('mcp.slack.call_tool', 'Call Slack tool', 'Post, read, or search via Slack MCP tools.'),
      tool('mcp.slack.list_tools', 'List Slack tools', 'Discover tools exposed by Slack MCP.'),
    ],
  },
  {
    id: 'notion',
    name: 'Notion',
    category: 'docs',
    description: 'Read and write Notion pages/databases via its remote MCP server.',
    serverUrl: 'https://mcp.notion.com/mcp',
    authHint: 'oauth_via_provider',
    setupSteps: [
      'Connect → MCP servers → Notion, then Connect to authorize AutoBlocks against your workspace.',
      'Token stored as MCP_NOTION_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.notion.call_tool', 'Call Notion tool', 'Query or update Notion pages/databases.'),
      tool('mcp.notion.list_tools', 'List Notion tools', 'Discover tools exposed by Notion MCP.'),
    ],
  },
  {
    id: 'github',
    name: 'GitHub',
    category: 'dev',
    description: 'Issues, PRs, repos, and code search via GitHub’s remote MCP server.',
    serverUrl: 'https://api.githubcopilot.com/mcp/',
    authHint: 'api_token',
    setupSteps: [
      'Create a GitHub personal access token with the scopes your workflow needs.',
      'Connect → MCP servers → GitHub, paste the token, then Connect.',
      'Token stored as MCP_GITHUB_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.github.call_tool', 'Call GitHub tool', 'Open issues/PRs, search code, read repo state.'),
      tool('mcp.github.list_tools', 'List GitHub tools', 'Discover tools exposed by GitHub MCP.'),
    ],
  },
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'payments',
    description: 'Customers, invoices, and payment data via Stripe’s remote MCP server.',
    serverUrl: 'https://mcp.stripe.com',
    authHint: 'api_token',
    setupSteps: [
      'Create a restricted Stripe API key scoped to what your workflow needs.',
      'Connect → MCP servers → Stripe, paste the key, then Connect.',
      'Token stored as MCP_STRIPE_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.stripe.call_tool', 'Call Stripe tool', 'Look up or mutate customers, invoices, charges.'),
      tool('mcp.stripe.list_tools', 'List Stripe tools', 'Discover tools exposed by Stripe MCP.'),
    ],
  },
  {
    id: 'linear',
    name: 'Linear',
    category: 'project_management',
    description: 'Issues, projects, and cycles via Linear’s remote MCP server.',
    serverUrl: 'https://mcp.linear.app/mcp',
    authHint: 'oauth_via_provider',
    setupSteps: [
      'Connect → MCP servers → Linear, then Connect to authorize AutoBlocks.',
      'Token stored as MCP_LINEAR_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.linear.call_tool', 'Call Linear tool', 'Create/update issues, read cycles and projects.'),
      tool('mcp.linear.list_tools', 'List Linear tools', 'Discover tools exposed by Linear MCP.'),
    ],
  },
  {
    id: 'asana',
    name: 'Asana',
    category: 'project_management',
    description: 'Tasks and projects via Asana’s remote MCP server.',
    serverUrl: 'https://mcp.asana.com/v2/mcp',
    authHint: 'oauth_via_provider',
    setupSteps: [
      'Connect → MCP servers → Asana, then Connect to authorize AutoBlocks.',
      'Token stored as MCP_ASANA_TOKEN in your vault.',
      'Uses Asana MCP v2 (streamable HTTP) — the old /sse beta endpoint is deprecated.',
    ],
    capabilities: [
      tool('mcp.asana.call_tool', 'Call Asana tool', 'Create/update tasks, read projects.'),
      tool('mcp.asana.list_tools', 'List Asana tools', 'Discover tools exposed by Asana MCP.'),
    ],
  },
  {
    id: 'atlassian',
    name: 'Atlassian (Jira/Confluence)',
    category: 'project_management',
    description: 'Jira issues and Confluence pages via Atlassian’s remote MCP server.',
    serverUrl: 'https://mcp.atlassian.com/v1/mcp/authv2',
    authHint: 'oauth_via_provider',
    setupSteps: [
      'Connect → MCP servers → Atlassian, then Connect to authorize AutoBlocks.',
      'Token stored as MCP_ATLASSIAN_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.atlassian.call_tool', 'Call Atlassian tool', 'Read/write Jira issues and Confluence pages.'),
      tool('mcp.atlassian.list_tools', 'List Atlassian tools', 'Discover tools exposed by Atlassian MCP.'),
    ],
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    category: 'crm',
    description: 'Contacts, deals, and companies via HubSpot’s remote MCP server.',
    serverUrl: 'https://mcp.hubspot.com',
    authHint: 'oauth_via_provider',
    setupSteps: [
      'Connect → MCP servers → HubSpot, then Connect to authorize AutoBlocks.',
      'Token stored as MCP_HUBSPOT_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.hubspot.call_tool', 'Call HubSpot tool', 'Read/update contacts, deals, companies.'),
      tool('mcp.hubspot.list_tools', 'List HubSpot tools', 'Discover tools exposed by HubSpot MCP.'),
    ],
  },
  {
    id: 'google-sheets',
    name: 'Google Sheets',
    category: 'data',
    description: 'Read and write spreadsheet data via a Google Sheets MCP server.',
    requiresCustomUrl: true,
    authHint: 'self_hosted',
    setupSteps: [
      'Deploy a Google Sheets MCP server (e.g. an open-source streamable-HTTP server) or point at one your team hosts.',
      'Connect → MCP servers → Google Sheets, paste the server URL and token, then Connect.',
      'Token stored as MCP_GOOGLE_SHEETS_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.google-sheets.call_tool', 'Call Sheets tool', 'Read/write ranges, rows, and values.'),
      tool('mcp.google-sheets.list_tools', 'List Sheets tools', 'Discover tools exposed by the Sheets MCP server.'),
    ],
  },
  {
    id: 'postgres',
    name: 'Postgres',
    category: 'data',
    description: 'Query and inspect a Postgres database via a self-hosted MCP server.',
    requiresCustomUrl: true,
    authHint: 'self_hosted',
    setupSteps: [
      'Run a Postgres MCP server against your database (self-hosted; the connection string never leaves your infra).',
      'Connect → MCP servers → Postgres, paste the server URL and token, then Connect.',
      'Token stored as MCP_POSTGRES_TOKEN in your vault.',
    ],
    capabilities: [
      tool('mcp.postgres.call_tool', 'Call Postgres tool', 'Run read queries or inspect schema.'),
      tool('mcp.postgres.list_tools', 'List Postgres tools', 'Discover tools exposed by the Postgres MCP server.'),
    ],
  },
];

export function getMcpPreset(id: string): McpServerPreset | undefined {
  return MCP_SERVER_PRESETS.find((p) => p.id === id);
}

export function mcpPresetConnectorId(id: string): string {
  return `ab.mcp.${id}`;
}

export function mcpPresetIdFromConnectorId(connectorId: string): string | undefined {
  const match = /^ab\.mcp\.(.+)$/.exec(connectorId);
  return match?.[1];
}
